import { ErrorMessages } from 'src/common/constants';
import moment, { Moment } from 'moment';
import {
  CsvKey,
  IndividualSuiteParsedFromCSV,
  ISuiteAsset,
  ProcessedSuite,
  TransformedSuiteItem,
} from 'src/pages/Suite/Components/UnitImportDialogue/Hooks/BulkSaveSuiteAPITypes';

const formatWarrantyDate = (dateString: string | undefined): Date | null => {
  if (!dateString?.trim()) {
    return null;
  }

  try {
    const momentDate: Moment = moment(dateString);

    if (!momentDate.isValid()) {
      return null;
    }

    return new Date(momentDate.format('l LT'));
  } catch {
    return null;
  }
};

const convertStringToArray = (targetString: string): string[] => {
  if (!targetString?.trim()) {
    return [];
  }

  const separator = targetString.includes('#') ? '#' : ',';

  return targetString
    .split(separator)
    .map((stall: string) => stall.trim())
    .filter((stall: string) => stall !== '');
};

const hasValue = (val: string | undefined | null): boolean => {
  return val !== undefined && val !== null && val.trim() !== '';
};

const cleanEmptyStringsToNull = (obj: any): any => {
  const cleaned: any = {};

  for (const [key, value] of Object.entries(obj)) {
    if (typeof value === 'string' && value.trim() === '') {
      cleaned[key] = null;
    } else {
      cleaned[key] = value;
    }
  }

  return cleaned;
};

const parseKeyAndValues = (keyValueString: string): Array<CsvKey> => {
  if (!keyValueString?.trim()) {
    return [];
  }

  // Split by # to get pairs: ["jacob|allen", "key|mike", ...]
  const pairs: string[] = keyValueString
    .split('#')
    .map((pair: string) => pair.trim())
    .filter((pair: string) => pair !== '');

  // Split each pair by | and create {Key, Value} objects.
  return pairs.map((pair: string) => {
    const [key, value] = pair.split('|').map((part: string) => part.trim());

    return {
      Key: key || '',
      Value: value || '',
    };
  });
};

const transformSuiteItem = (
  item: IndividualSuiteParsedFromCSV
): TransformedSuiteItem => {
  const cleanedItem: any = cleanEmptyStringsToNull(item);

  const result: any = { ...cleanedItem };

  if (hasValue(cleanedItem.WarrantyDefaultStartDate)) {
    result.WarrantyDefaultStartDate = formatWarrantyDate(
      cleanedItem.WarrantyDefaultStartDate
    );
  }

  if (hasValue(cleanedItem.ParkingStalls)) {
    result.ParkingStalls = convertStringToArray(cleanedItem.ParkingStalls);
  }

  if (hasValue(cleanedItem.FobNumbers)) {
    result.FobNumbers = convertStringToArray(cleanedItem.FobNumbers);
  }

  if (hasValue(cleanedItem.StorageLockers)) {
    result.StorageLockers = convertStringToArray(cleanedItem.StorageLockers);
  }

  if (hasValue(cleanedItem.NoOfKeys)) {
    const parsed: number = parseInt(cleanedItem.NoOfKeys, 10);
    result.NoOfKeys = isNaN(parsed) ? null : parsed;
  }

  if (hasValue(cleanedItem.KeyAndValues)) {
    result.KeyAndValues = parseKeyAndValues(cleanedItem.KeyAndValues);
  }

  return result as TransformedSuiteItem;
};

/* Reconstructs the nested tree structure (Suite -> Areas -> Assets) from flat CSV rows.
 * Complexity: O(n) - Single-pass efficiency (no more post-processing loop required). */
const groupSuitesByNumber = (transformedSuites: TransformedSuiteItem[]) => {
  const suiteMap = new Map<string, ProcessedSuite>();

  for (const suite of transformedSuites) {
    const buildingStr: string = suite.Building ? suite.Building.trim() : '';
    const numberStr: string = suite.Number ? suite.Number.trim() : '';
    const key: string = `${buildingStr}-${numberStr}`;

    const foundSuite: ProcessedSuite = suiteMap.get(key);
    const rawAssets: string = suite.Assets || '';

    const parsedAssets: ISuiteAsset[] = convertStringToArray(rawAssets).map(
      (name: string) => ({ Name: name })
    );

    if (!foundSuite) {
      const areaName: string = suite.Areas || suite.Rooms || '';
      const { Assets, Areas, Rooms, ...otherProps }: TransformedSuiteItem =
        suite;

      const newSuite: ProcessedSuite = {
        ...otherProps,
        Areas: [
          {
            Name: areaName,
            Assets: parsedAssets,
          },
        ],
      };

      suiteMap.set(key, newSuite);
    } else {
      const areaName: string = suite.Areas || suite.Rooms;
      const areaExists: any = foundSuite.Areas.some(
        (area: any) => area.Name === suite.Areas
      );

      if (!areaExists) {
        foundSuite.Areas.push({
          Name: areaName,
          Assets: parsedAssets,
        });
      }
    }
  }

  return Array.from(suiteMap.values());
};

const processSuiteDataFromCsv = (
  validatedSuites: IndividualSuiteParsedFromCSV[]
): ProcessedSuite[] => {
  const transformedSuites: TransformedSuiteItem[] = validatedSuites.map(
    (suite: IndividualSuiteParsedFromCSV) => {
      return transformSuiteItem(suite);
    }
  );

  const groupedSuites: ProcessedSuite[] =
    groupSuitesByNumber(transformedSuites);

  return groupedSuites;
};

// Main ETL entry point: Validates and orchestrates the CSV-to-JSON transformation.
const transformSuiteDataFromCSV = (
  resultArr: IndividualSuiteParsedFromCSV[]
): ProcessedSuite[] => {
  try {
    if (!resultArr || resultArr.length === 0) {
      throw new Error(ErrorMessages.noDatacsv);
    }

    return processSuiteDataFromCsv(resultArr);
  } catch (error) {
    console.error('Error processing suite data from CSV:', error);
    throw error;
  }
};

export {
  transformSuiteDataFromCSV,
  formatWarrantyDate,
  transformSuiteItem,
  groupSuitesByNumber,
  processSuiteDataFromCsv,
  parseKeyAndValues
};
